import { motion } from 'framer-motion';
import useUser from 'hooks/useUser';
import { useEffect, useState } from 'react';
import ConfirmationModal from 'components/ui/ConfirmationModal';
import SkeletonWrapper from 'components/ui/SkeletonWrapper';
import {
  useApproveEmployeeLeaveMutation,
  useGetEmployeeLeavesByYearQuery,
  useGetEmployeeLeavesStatsQuery,
  useRejectEmployeeLeaveMutation,
} from 'redux/features/employeeLeaveApi';
import { useGetEmployeeEventsByMonthQuery } from 'redux/features/admin/adminApi';
import { useGetSettingsQuery } from 'redux/features/settings/settingsApi';
import { LEAVE_TYPES } from 'utils/common';
import Icon from '../../components/AppIcon';
import CommandPalette from '../../components/ui/CommandPalette';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import {
  useDeleteHolidayMutation,
  useGetHolidaysByYearQuery,
  useUpdateHolidayMutation,
} from '../../redux/features/holidayApi';
import ApproveLeaveModal from '../admin-leave-management/components/ApproveLeaveModal';
import RejectLeaveModal from '../admin-leave-management/components/RejectLeaveModal';
import LeaveSummaryPanel from '../holiday-management-employee-view/components/LeaveSummaryPanel';
import AddEventModal from './components/AddEventModal';
import CalendarGrid from './components/CalendarGrid';
import EventListModal from './components/EventListModal';
import OfficialLeaveModal from './components/OfficialLeaveModal';
// Import leave API if available
// import { useGetLeavesQuery, useUpdateLeaveMutation } from '../../api/leaveApi';

const HolidayManagementAdminView = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [holidays, setHolidays] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [selectedEmployeeEvents, setSelectedEmployeeEvents] = useState([]);
  const [selectedLeaveFilter, setSelectedLeaveFilter] = useState('PENDING');
  const [selectedEventIndex, setSelectedEventIndex] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isOfficialCheckoutModalOpen, setIsOfficialCheckoutModalOpen] = useState(false);
  const user = useUser();

  // Approve/Reject modal states
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedLeaveForAction, setSelectedLeaveForAction] = useState(null);

  // Desktop right panel toggle state - closed by default on mobile/tablet
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(() => {
    return window.innerWidth >= 1024;
  });

  // Calendar filter states with localStorage persistence
  const [calendarFilters, setCalendarFilters] = useState(() => {
    return {
      eventCelebration: true,
      fullHoliday: true,
      halfHoliday: true,
      myLeaves: true,
      saturdayOff: true,
      birthday: true,
      anniversary: true,
    };
  });

  const currentEmployeeId = user?.employee_id || 'EMP000';
  const isAdmin = user?.role === 'ADMIN' || currentEmployeeId === 'EMP000';

  // API Hooks
  const {
    data: holidaysData,
    isLoading: isLoadingHolidays,
    refetch: refetchHolidays,
  } = useGetHolidaysByYearQuery({
    year: currentDate.getFullYear(),
  });

  const [updateHoliday] = useUpdateHolidayMutation();
  const [deleteHoliday] = useDeleteHolidayMutation();
  const { data: employeeLeavesStats } = useGetEmployeeLeavesStatsQuery({
    year: currentDate.getFullYear(),
  });
  const { data: leavesData } = useGetEmployeeLeavesByYearQuery({
    year: currentDate.getFullYear(),
  });
  const [approveLeave, { isLoading: isApproving }] = useApproveEmployeeLeaveMutation();
  const [rejectLeave] = useRejectEmployeeLeaveMutation();
  const { data: employeeEventsByMonth } = useGetEmployeeEventsByMonthQuery({
    month: currentDate.getMonth() + 1,
    year: currentDate.getFullYear(),
  });
  const { data: settingsData } = useGetSettingsQuery();
  const employeeBirthdays = employeeEventsByMonth?.data?.birthdays || [];
  const employeeWorkAnniversaries = employeeEventsByMonth?.data?.anniversaries || [];

  const [workSchedule, setWorkScheduleState] = useState({
    dayStartTime: '',
    dayEndTime: '',
    breakResumeTime: '',
  });

  // If you have a leave API, use it here
  // const { data: leavesData, isLoading: isLoadingLeaves, refetch: refetchLeaves } = useGetLeavesQuery();

  // Persist filter changes to localStorage
  useEffect(() => {
    localStorage.setItem('adminCalendarFilters', JSON.stringify(calendarFilters));
  }, [calendarFilters]);

  // Handle responsive panel behavior on window resize
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      if (!isDesktop && isRightPanelOpen) {
        setIsRightPanelOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isRightPanelOpen]);

  const handleFilterChange = (filterType, value) => {
    setCalendarFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleApproveClick = (leave) => {
    const fullLeave = typeof leave === 'object' ? leave : leavesData?.find((l) => l.id === leave);
    setSelectedLeaveForAction(fullLeave);
    setApproveModalOpen(true);
  };

  const handleRejectClick = (leave) => {
    const fullLeave = typeof leave === 'object' ? leave : leavesData?.find((l) => l.id === leave);
    setSelectedLeaveForAction(fullLeave);
    setRejectModalOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedLeaveForAction) return;

    try {
      await approveLeave(selectedLeaveForAction.id).unwrap();
      setApproveModalOpen(false);
      setSelectedLeaveForAction(null);
    } catch (error) {
      console.error('Failed to approve leave:', error);
      alert('Failed to approve leave. Please try again.');
    }
  };

  const handleConfirmReject = async (reason) => {
    if (!selectedLeaveForAction) return;

    try {
      await rejectLeave({ id: selectedLeaveForAction.id, reason }).unwrap();
      setRejectModalOpen(false);
      setSelectedLeaveForAction(null);
    } catch (error) {
      console.error('Failed to reject leave:', error);
      alert('Failed to reject leave. Please try again.');
    }
  };

  const handleApproveLeave = async (leaveId) => {
    handleApproveClick(leaveId);
  };

  const handleRejectLeave = async (leaveId) => {
    handleRejectClick(leaveId);
  };

  const handleCancelLeave = async (leaveId) => {
    // If you have leave API, use it here
    // await deleteLeave(leaveId);

    // For now, using localStorage
    const savedLeaves = localStorage.getItem('employeeLeaves') || '[]';
    const allLeaves = JSON.parse(savedLeaves);

    const updatedAllLeaves = allLeaves.filter((leave) => leave.id !== leaveId);
    localStorage.setItem('employeeLeaves', JSON.stringify(updatedAllLeaves));
  };

  const getDateKey = (date) => {
    return `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}-${String(date?.getDate())?.padStart(2, '0')}`;
  };

  useEffect(() => {
    // Process holidays data from API
    if (holidaysData) {
      const formattedHolidays = {};

      // Assuming holidaysData is an array of holidays
      if (Array.isArray(holidaysData?.holidays)) {
        holidaysData?.holidays?.forEach((holiday) => {
          const dateKey = getDateKey(new Date(holiday.date));
          if (!formattedHolidays[dateKey]) {
            formattedHolidays[dateKey] = [];
          }
          formattedHolidays[dateKey].push({
            id: holiday.id,
            title: holiday.title || holiday.name,
            type: holiday.type || 'Full Holiday',
            description: holiday.description,
          });
        });
      }

      setHolidays(formattedHolidays);
    }
    if (settingsData?.data) {
      setWorkScheduleState({
        dayStartTime: settingsData?.data?.day_start_time,
        dayEndTime: settingsData?.data?.day_end_time,
        breakResumeTime: settingsData?.data?.break_start_time,
      });
    }
  }, [holidaysData, settingsData]);

  const handleDateClick = (date, events, leaves, employeeEvents) => {
    setSelectedDate(date);
    setSelectedEvents(events);
    setSelectedEmployeeEvents(employeeEvents || []);
    setSelectedLeaves(leaves || []);

    if (events?.length > 0 || leaves?.length > 0 || employeeEvents?.length > 0) {
      setIsListModalOpen(true);
    } else {
      setIsAddModalOpen(true);
    }
  };

  const formattedDate = selectedDate
    ? new Date(selectedDate - selectedDate.getTimezoneOffset() * 60000).toISOString()
    : new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString();

  const handleEditEvent = async (eventIndex, updatedEvent) => {
    try {
      const dateKey = getDateKey(selectedDate);
      const event = holidays?.[dateKey]?.[eventIndex];

      if (!event?.id) {
        console.error('Event ID not found');
        return;
      }

      const payload = {
        id: event.id,
        date: formattedDate,
        title: updatedEvent.title,
        type: updatedEvent.type,
      };

      await updateHoliday(payload).unwrap();

      // Refetch holidays to get updated list
      refetchHolidays();

      // Update selected events for immediate UI update
      const updatedEvents = [...selectedEvents];
      updatedEvents[eventIndex] = { ...updatedEvents[eventIndex], ...updatedEvent };
      setSelectedEvents(updatedEvents);
    } catch (error) {
      console.error('Failed to update holiday:', error);
      // Handle error - show toast/notification
    }
  };

  const handleDeleteEvent = async () => {
    try {
      const dateKey = getDateKey(selectedDate);
      const event = holidays?.[dateKey]?.[selectedEventIndex];

      if (!event?.id) {
        console.error('Event ID not found');
        return;
      }

      await deleteHoliday(event.id).unwrap();

      // Refetch holidays to get updated list
      refetchHolidays();

      // Update selected events for immediate UI update
      const updatedEvents = selectedEvents.filter((_, index) => index !== selectedEventIndex);
      setSelectedEvents(updatedEvents);
      setIsConfirmationModalOpen(false);
    } catch (error) {
      console.error('Failed to delete holiday:', error);
      // Handle error - show toast/notification
    }
  };

  const handleAddFromList = (date) => {
    setIsListModalOpen(false);
    setSelectedDate(date);
    setIsAddModalOpen(true);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value);
    const newDate = new Date(currentDate);
    newDate.setMonth(newMonth);
    setCurrentDate(newDate);
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    const newDate = new Date(currentDate);
    newDate.setFullYear(newYear);
    setCurrentDate(newDate);
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const currentYear = currentDate?.getFullYear();
  const currentMonth = currentDate?.getMonth();

  // Generate year options (current year ± 10 years)
  const currentYearValue = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYearValue - 10; year <= currentYearValue + 10; year++) {
    yearOptions.push(year);
  }

  // Count total events for statistics for the current year only
  const getTotalEventCounts = (year) => {
    let totalEvents = 0;
    let celebrations = 0;
    let fullHolidays = 0;
    let halfHolidays = 0;
    let saturdayOffs = 0;

    Object.keys(holidays)?.forEach((dateKey) => {
      const [eventYear] = dateKey.split('-');
      if (parseInt(eventYear) === year) {
        const dayEvents = holidays[dateKey];
        if (Array.isArray(dayEvents)) {
          totalEvents += dayEvents?.length;
          dayEvents?.forEach((event) => {
            switch (event?.type) {
              case 'EVENT_CELEBRATION':
                celebrations++;
                break;
              case 'FULL_HOLIDAY':
                fullHolidays++;
                break;
              case 'HALF_HOLIDAY':
                halfHolidays++;
                break;
              case 'SATURDAY_OFF':
                saturdayOffs++;
                break;
              default:
                break;
            }
          });
        }
      }
    });

    return { totalEvents, celebrations, fullHolidays, halfHolidays, saturdayOffs };
  };

  const eventCounts = getTotalEventCounts(currentYear);

  const getStartTimeDisplay = (leave) => {
    // Handle datetime-based times
    if (leave?.start_date_time) {
      const date = new Date(leave?.start_date_time);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString([], {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }

    // Handle multiple day leaves - show first day
    if (leave?.dates && leave?.dates?.length > 0) {
      const firstDate = new Date(leave?.dates?.[0]);
      if (!isNaN(firstDate.getTime())) {
        // For multiple day leaves, show first day with start time
        const startTime =
          leave?.type === LEAVE_TYPES.HALF_DAY
            ? leave?.halfDayType === 'FIRST_HALF'
              ? workSchedule?.dayStartTime
              : workSchedule?.breakResumeTime
            : workSchedule?.dayStartTime;
        return `${firstDate.toLocaleDateString()} ${startTime}`;
      }
    }

    return 'N/A';
  };

  const getEndTimeDisplay = (leave) => {
    // Handle datetime-based times
    if (leave?.end_date_time) {
      const date = new Date(leave?.end_date_time);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString([], {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }

    // Handle multiple day leaves - show last day
    if (leave?.dates && leave?.dates?.length > 0) {
      const sortedDates = [...leave.dates].sort();
      const lastDate = new Date(sortedDates?.[sortedDates?.length - 1]);
      if (!isNaN(lastDate.getTime())) {
        // For multiple day leaves, show last day with end time
        const endTime =
          leave?.type === LEAVE_TYPES.HALF_DAY
            ? leave?.halfDayType === 'FIRST_HALF'
              ? workSchedule?.breakResumeTime
              : workSchedule?.dayEndTime
            : workSchedule?.dayEndTime;
        return `${lastDate.toLocaleDateString()} ${endTime}`;
      }
    }

    return 'N/A';
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Header />
      <Sidebar />
      <CommandPalette />
      <OfficialLeaveModal
        isOpen={isOfficialCheckoutModalOpen}
        onClose={() => setIsOfficialCheckoutModalOpen(false)}
      />
      {/* Main Content */}
      <div className="lg:ml-60 pt-16 overflow-x-hidden">
        <div className="flex">
          {/* Left Panel - Calendar */}
          <motion.div
            className={`flex-1 px-4 sm:px-6 lg:px-8 py-4 lg:py-8 ${
              isRightPanelOpen ? 'min-w-0' : 'min-w-0'
            }`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Page Header */}
            <motion.div className="mb-6 lg:mb-8" variants={itemVariants}>
              <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-text-primary mb-2">
                Calendar Management
              </h1>
              <p className="text-text-secondary text-sm lg:text-base">
                Manage company holidays and events
              </p>
            </motion.div>

            {/* Statistics Cards - Auto-adjustable Flex */}
            <motion.div
              className="flex flex-wrap gap-3 lg:gap-4 mb-4 lg:mb-6"
              variants={itemVariants}
            >
              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={isLoadingHolidays}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-primary-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Calendar" size={16} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Total Events
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {eventCounts?.totalEvents}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={isLoadingHolidays}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[140px] p-2 lg:p-3' : 'min-w-[160px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-blue-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Star" size={16} color="#3B82F6" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Celebrations
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {eventCounts?.celebrations}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>
              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={isLoadingHolidays}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-red-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Calendar" size={16} color="#EF4444" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Full Holidays
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {eventCounts?.fullHolidays}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>
              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={isLoadingHolidays}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-orange-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Clock" size={16} color="#F97316" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Half Holidays
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {eventCounts?.halfHolidays}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>
              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={isLoadingHolidays}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-green-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Home" size={16} color="#10B981" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Saturday Off
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {eventCounts?.saturdayOffs}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>
            </motion.div>

            {/* Calendar Navigation & Filters */}
            <motion.div
              className="bg-surface rounded-lg border border-border p-4 lg:p-6 mb-4 lg:mb-6"
              variants={itemVariants}
            >
              <div className="flex flex-col space-y-4">
                {/* Calendar Navigation */}
                <div className="flex flex-wrap items-center justify-between gap-2 lg:gap-4">
                  {/* Left side - Navigation controls */}
                  <div className="flex flex-wrap items-center gap-2 lg:gap-4">
                    {/* Previous Month Button */}
                    <button
                      onClick={() => navigateMonth(-1)}
                      className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                      title="Previous month"
                    >
                      <Icon name="ChevronLeft" size={20} />
                    </button>

                    {/* Month and Year Selectors */}
                    <div className="flex items-center space-x-1 lg:space-x-3">
                      <div className="relative">
                        <select
                          value={currentMonth}
                          onChange={handleMonthChange}
                          className="appearance-none px-2 sm:px-3 lg:px-4 py-2 pr-6 sm:pr-8 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background text-text-primary font-medium min-w-[70px] sm:min-w-[90px] lg:min-w-[110px] text-xs sm:text-sm lg:text-base [&::-webkit-calendar-picker-indicator]:hidden [&::-ms-expand]:hidden"
                          style={{ backgroundImage: 'none' }}
                        >
                          {monthNames?.map((month, index) => (
                            <option key={index} value={index}>
                              {month}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-1 sm:pr-2 pointer-events-none">
                          <Icon
                            name="ChevronDown"
                            size={14}
                            className="sm:w-4 sm:h-4"
                            color="var(--color-text-secondary)"
                          />
                        </div>
                      </div>

                      <div className="relative">
                        <select
                          value={currentYear}
                          onChange={handleYearChange}
                          className="appearance-none px-2 sm:px-3 lg:px-4 py-2 pr-6 sm:pr-8 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background text-text-primary font-medium min-w-[50px] sm:min-w-[70px] lg:min-w-[90px] text-xs sm:text-sm lg:text-base [&::-webkit-calendar-picker-indicator]:hidden [&::-ms-expand]:hidden"
                          style={{ backgroundImage: 'none' }}
                        >
                          {yearOptions?.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-1 sm:pr-2 pointer-events-none">
                          <Icon
                            name="ChevronDown"
                            size={14}
                            className="sm:w-4 sm:h-4"
                            color="var(--color-text-secondary)"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Next Month Button */}
                    <button
                      onClick={() => navigateMonth(1)}
                      className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                      title="Next month"
                    >
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </div>

                  {/* Right side - Today and Add Event Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={goToToday}
                      className="px-2 sm:px-3 lg:px-4 py-2 text-primary bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors duration-200 text-xs sm:text-sm lg:text-base flex-shrink-0"
                    >
                      Today
                    </button>
                    <button
                      onClick={() => setIsAddModalOpen(true)}
                      className="px-2 sm:px-3 lg:px-4 py-2 text-white bg-primary hover:bg-primary-600 rounded-lg transition-colors duration-200 text-xs sm:text-sm lg:text-base flex-shrink-0 flex items-center gap-1"
                    >
                      <Icon name="Plus" size={14} />
                      Add Event
                    </button>
                    <button
                      onClick={() => setIsOfficialCheckoutModalOpen(true)}
                      className="px-2 sm:px-3 lg:px-4 py-2 text-white bg-primary hover:bg-primary-600 rounded-lg transition-colors duration-200 text-xs sm:text-sm lg:text-base flex-shrink-0 flex items-center gap-1"
                    >
                      <Icon name="LogOut" size={14} />
                      Official Leave
                    </button>
                  </div>
                </div>

                {/* Horizontal Divider */}
                <div className="border-b border-border"></div>

                {/* Calendar Filters */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 lg:gap-4 overflow-x-auto">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="eventCelebration"
                      checked={calendarFilters?.eventCelebration}
                      onChange={(e) => handleFilterChange('eventCelebration', e?.target?.checked)}
                      className="w-4 h-4 text-blue-600 bg-background border-border rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label
                      htmlFor="eventCelebration"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Event Celebration
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="fullHoliday"
                      checked={calendarFilters?.fullHoliday}
                      onChange={(e) => handleFilterChange('fullHoliday', e?.target?.checked)}
                      className="w-4 h-4 text-red-600 bg-background border-border rounded focus:ring-red-500 focus:ring-2"
                    />
                    <label
                      htmlFor="fullHoliday"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Full Holiday
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="halfHoliday"
                      checked={calendarFilters?.halfHoliday}
                      onChange={(e) => handleFilterChange('halfHoliday', e?.target?.checked)}
                      className="w-4 h-4 text-orange-600 bg-background border-border rounded focus:ring-orange-500 focus:ring-2"
                    />
                    <label
                      htmlFor="halfHoliday"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Half Holiday
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="myLeaves"
                      checked={calendarFilters?.myLeaves}
                      onChange={(e) => handleFilterChange('myLeaves', e?.target?.checked)}
                      className="w-4 h-4 text-purple-600 bg-background border-border rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <label
                      htmlFor="myLeaves"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Employee Leaves
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="saturdayOff"
                      checked={calendarFilters?.saturdayOff}
                      onChange={(e) => handleFilterChange('saturdayOff', e?.target?.checked)}
                      className="w-4 h-4 text-green-600 bg-background border-border rounded focus:ring-green-500 focus:ring-2"
                    />
                    <label
                      htmlFor="saturdayOff"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Saturday Off
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="birthday"
                      checked={calendarFilters?.birthday}
                      onChange={(e) => handleFilterChange('birthday', e?.target?.checked)}
                      className="w-4 h-4 text-yellow-600 bg-background border-border rounded focus:ring-yellow-500 focus:ring-2"
                    />
                    <label
                      htmlFor="birthday"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Birthday
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="anniversary"
                      checked={calendarFilters?.anniversary}
                      onChange={(e) => handleFilterChange('anniversary', e?.target?.checked)}
                      className="w-4 h-4 text-cyan-600 bg-background border-border rounded focus:ring-cyan-500 focus:ring-2"
                    />
                    <label
                      htmlFor="anniversary"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Work Anniversary
                    </label>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Calendar Grid */}
            <motion.div variants={itemVariants}>
              <CalendarGrid
                employeeBirthdays={employeeBirthdays}
                employeeWorkAnniversaries={employeeWorkAnniversaries}
                currentDate={currentDate}
                holidays={holidays}
                leaves={leavesData || []}
                onDateClick={handleDateClick}
                calendarFilters={calendarFilters}
              />
            </motion.div>

            {/* Instructions */}
            <motion.div
              className="mt-4 lg:mt-6 bg-secondary-50 border border-secondary-200 rounded-lg p-3 lg:p-4"
              variants={itemVariants}
            >
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} color="var(--color-text-secondary)" />
                <div className="text-xs lg:text-sm text-text-secondary">
                  <p className="font-medium mb-1">Information:</p>
                  <ul className="space-y-1">
                    <li>• Click on dates with events to view and manage existing events</li>
                    <li>• Click on empty dates to add new events</li>
                    <li>• Use filters to control what appears on calendar</li>
                    <li>• Purple markers show employee leaves</li>
                    <li>• Use the right panel to manage leave applications</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Panel Toggle Button */}
          <button
            onClick={() => setIsRightPanelOpen(!isRightPanelOpen)}
            className={`absolute top-20 z-40 bg-primary text-white p-3 shadow-lg hover:bg-primary-600 transition-all duration-300 ${
              isRightPanelOpen ? 'right-4 lg:right-80 rounded-l-lg' : 'right-4 rounded-l-lg'
            }`}
            title={isRightPanelOpen ? 'Hide Leave Management Panel' : 'Show Leave Management Panel'}
          >
            <Icon name={isRightPanelOpen ? 'ChevronRight' : 'ChevronLeft'} size={20} />
          </button>

          {/* Mobile Overlay */}
          {isRightPanelOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
              onClick={() => setIsRightPanelOpen(false)}
            />
          )}

          {/* Right Panel - Leave Management */}
          <div
            className={`transition-all duration-300 ease-in-out ${
              isRightPanelOpen
                ? 'w-80 opacity-100 translate-x-0'
                : 'w-0 opacity-0 translate-x-full overflow-hidden'
            } ${'hidden lg:block lg:flex-shrink-0'}`}
          >
            <div
              className={`transition-all duration-300 ease-in-out h-full ${
                isRightPanelOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
              }`}
            >
              <div className="h-full overflow-y-auto">
                <LeaveSummaryPanel
                  employeeLeavesStats={employeeLeavesStats}
                  leaves={leavesData || []}
                  selectedFilter={selectedLeaveFilter}
                  onFilterChange={setSelectedLeaveFilter}
                  onCancelLeave={isAdmin ? null : handleCancelLeave}
                  onApproveLeave={isAdmin ? handleApproveLeave : null}
                  onRejectLeave={isAdmin ? handleRejectLeave : null}
                  currentYear={currentYear}
                  isAdmin={isAdmin}
                />
              </div>
            </div>
          </div>

          {/* Mobile Drawer */}
          {isRightPanelOpen && (
            <div className="lg:hidden fixed right-0 top-16 bottom-0 w-80 bg-surface border-l border-border z-40 transform transition-transform duration-300 ease-in-out overflow-y-auto">
              <LeaveSummaryPanel
                employeeLeavesStats={employeeLeavesStats}
                leaves={leavesData || []}
                selectedFilter={selectedLeaveFilter}
                onFilterChange={setSelectedLeaveFilter}
                onCancelLeave={isAdmin ? null : handleCancelLeave}
                onApproveLeave={isAdmin ? handleApproveLeave : null}
                onRejectLeave={isAdmin ? handleRejectLeave : null}
                currentYear={currentYear}
                isAdmin={isAdmin}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddEventModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        selectedDate={selectedDate}
        onSuccess={() => {
          refetchHolidays();
        }}
      />

      <ApproveLeaveModal
        isOpen={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        leave={selectedLeaveForAction}
        onConfirm={handleConfirmApprove}
        isLoading={isApproving}
        getStartTimeDisplay={getStartTimeDisplay}
        getEndTimeDisplay={getEndTimeDisplay}
      />
      <RejectLeaveModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        leaveDetails={selectedLeaveForAction}
        type="reject"
      />
      <EventListModal
        isOpen={isListModalOpen}
        onClose={() => setIsListModalOpen(false)}
        selectedDate={selectedDate}
        events={selectedEvents}
        leaves={selectedLeaves}
        employeeEvents={selectedEmployeeEvents}
        onEdit={handleEditEvent}
        onDelete={(index) => {
          setSelectedEventIndex(index);
          setIsConfirmationModalOpen(true);
        }}
        onAdd={handleAddFromList}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleDeleteEvent}
        title="Delete Holiday"
        message="Are you sure you want to delete this holiday?"
      />
    </div>
  );
};

export default HolidayManagementAdminView;
